import type { FormResponsesApi } from "@/modules/forms/presentation/api/form-responses-api";
import type {
  FormFieldDTO,
  FormResponsesListDTO,
  FormResponsesPrintDataDTO,
  FormResponsesSummaryDTO,
} from "@/modules/forms/presentation/api/types";
import { config } from "@/shared/lib/config";
import { handleError } from "@/shared/lib/fetcher";

export type FormViewerMetaDTO = {
  formId: string;
  title: string;
  viewerPinConfigured: boolean;
  hasResponses: boolean;
  canView: boolean;
  sessionValid: boolean;
  viewerLinkExpired: boolean;
  viewerAccessExpiresAt: string | null;
};

async function parseJsonError(res: Response): Promise<never> {
  let code = "UNKNOWN";
  try {
    const raw: unknown = await res.json();
    if (raw !== null && typeof raw === "object" && "error" in raw) {
      const e = (raw as { error: unknown }).error;
      if (typeof e === "string") code = e;
    }
  } catch {
    /* omit */
  }
  const err = new Error(code) as Error & { code: string; httpStatus?: number };
  err.code = code;
  err.httpStatus = res.status;
  throw err;
}

function viewBase(formId: string) {
  return `${config.apiUrl}/public/forms/${encodeURIComponent(formId)}/view`;
}

export const publicFormsViewerApi = {
  async getMeta(formId: string): Promise<FormViewerMetaDTO> {
    const res = await fetch(`${viewBase(formId)}/meta`, {
      credentials: "include",
    });
    if (!res.ok) throw await parseJsonError(res);
    return (await res.json()) as FormViewerMetaDTO;
  },

  async verify(formId: string, pin: string): Promise<{ ok: true }> {
    const res = await fetch(`${viewBase(formId)}/verify`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pin }),
    });
    if (!res.ok) throw await parseJsonError(res);
    return (await res.json()) as { ok: true };
  },

  async logout(formId: string): Promise<void> {
    await fetch(`${viewBase(formId)}/logout`, {
      method: "POST",
      credentials: "include",
    });
  },

  async getContext(formId: string): Promise<{
    form: { id: string; title: string };
    fields: FormFieldDTO[];
  }> {
    const res = await fetch(`${viewBase(formId)}/context`, {
      credentials: "include",
    });
    if (!res.ok) throw await parseJsonError(res);
    return (await res.json()) as {
      form: { id: string; title: string };
      fields: FormFieldDTO[];
    };
  },
};

function viewerGet<T>(url: string): Promise<T> {
  return fetch(url, { credentials: "include" }).then(async (res) => {
    if (!res.ok) throw await parseJsonError(res);
    return (await res.json()) as T;
  });
}

export const viewerFormResponsesApi: FormResponsesApi = {
  listResponses(formId, args) {
    const url = new URL(`${viewBase(formId)}/responses`);
    url.searchParams.set("limit", String(args.limit));
    url.searchParams.set("offset", String(args.offset));
    if (args.filt?.trim()) url.searchParams.set("filt", args.filt.trim());
    return viewerGet<FormResponsesListDTO>(url.toString());
  },

  getResponsesSummary(formId) {
    return viewerGet<FormResponsesSummaryDTO>(
      `${viewBase(formId)}/responses/summary`,
    );
  },

  fetchResponsesPrintData(formId, options) {
    const url = new URL(`${viewBase(formId)}/responses/print-data`);
    if (options?.filt?.trim())
      url.searchParams.set("filt", options.filt.trim());
    if (options?.columnFieldIds?.length) {
      url.searchParams.set("columns", options.columnFieldIds.join(","));
    }
    return viewerGet<FormResponsesPrintDataDTO>(url.toString());
  },

  async downloadResponsesCsv(formId, options) {
    const url = new URL(`${viewBase(formId)}/responses/export.csv`);
    if (options?.filt?.trim())
      url.searchParams.set("filt", options.filt.trim());
    const res = await fetch(url.toString(), { credentials: "include" });
    if (!res.ok) throw await parseJsonError(res);
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = options?.filt?.trim()
      ? `form-${formId}-responses-filtered.csv`
      : `form-${formId}-responses.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  },

  getAnswerFileReadUrl(formId, key) {
    return fetch(`${viewBase(formId)}/responses/file-read-url`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key }),
    }).then(async (res) => {
      if (!res.ok) {
        await handleError(res);
        throw new Error("FILE_READ_FAILED");
      }
      return (await res.json()) as { url: string };
    });
  },
};

export function describeViewerError(code: string): string {
  switch (code) {
    case "VIEWER_UNAVAILABLE":
      return "ລິ້ງນີ້ບໍ່ພ້ອມໃຊ້ງານ";
    case "VIEWER_ACCESS_DENIED":
      return "ລະຫັດ PIN ບໍ່ຖືກຕ້ອງ ຫຼື ຍັງເປີດໃຫ້ເບິ່ງບໍ່ໄດ້";
    case "VIEWER_SESSION_REQUIRED":
      return "ກະລຸນາໃສ່ PIN ອີກຄັ້ງ";
    case "VIEWER_LINK_EXPIRED":
      return "ລິ້ງເບິ່ງຄຳຕອບນີ້ຫມົດອາຍຸແລ້ວ — ຕິດຕໍ່ຜູ້ຈັດຟອມ";
    default:
      return "ບໍ່ສຳເລັດ — ລອງອີກຄັ້ງ";
  }
}
